import { DifficultyLevel } from '../interfaces/question.interface';
import { TrekRound } from '../interfaces/fraction-trek.interface';

const MAX_TICK = 5;

/**
 * Curated add/subtract-on-a-number-line problems (Grade 4: like
 * denominators). Authored as mixed numbers; converted to d-ths below.
 * A mix of same-whole answers and regrouping (borrow / cross-a-whole)
 * cases so difficulty ramps.
 */
interface Spec { aW: number; aN: number; op: '+' | '-'; bW: number; bN: number; d: number; }

const POOL: Spec[] = [
  // ── Subtraction ──
  { aW: 2, aN: 2, op: '-', bW: 1, bN: 1, d: 5 }, // 2 2/5 − 1 1/5 = 1 1/5
  { aW: 2, aN: 2, op: '-', bW: 1, bN: 1, d: 3 }, // 2 2/3 − 1 1/3 = 1 1/3
  { aW: 1, aN: 3, op: '-', bW: 0, bN: 2, d: 4 }, // 1 3/4 − 2/4  = 1 1/4
  { aW: 3, aN: 3, op: '-', bW: 1, bN: 2, d: 5 }, // 3 3/5 − 1 2/5 = 2 1/5
  { aW: 2, aN: 1, op: '-', bW: 0, bN: 3, d: 4 }, // 2 1/4 − 3/4  = 1 2/4 (borrow)
  { aW: 3, aN: 1, op: '-', bW: 1, bN: 2, d: 3 }, // 3 1/3 − 1 2/3 = 1 2/3 (borrow)
  { aW: 4, aN: 2, op: '-', bW: 2, bN: 3, d: 5 }, // 4 2/5 − 2 3/5 = 1 4/5 (borrow)
  { aW: 2, aN: 1, op: '-', bW: 1, bN: 1, d: 2 }, // 2 1/2 − 1 1/2 = 1 (whole)

  // ── Addition ──
  { aW: 1, aN: 1, op: '+', bW: 2, bN: 1, d: 4 }, // 1 1/4 + 2 1/4 = 3 2/4
  { aW: 2, aN: 1, op: '+', bW: 1, bN: 1, d: 3 }, // 2 1/3 + 1 1/3 = 3 2/3
  { aW: 1, aN: 3, op: '+', bW: 1, bN: 1, d: 5 }, // 1 3/5 + 1 1/5 = 2 4/5
  { aW: 1, aN: 3, op: '+', bW: 0, bN: 2, d: 4 }, // 1 3/4 + 2/4  = 2 1/4 (cross whole)
  { aW: 1, aN: 1, op: '+', bW: 1, bN: 1, d: 2 }, // 1 1/2 + 1 1/2 = 3 (whole)
  { aW: 2, aN: 2, op: '+', bW: 1, bN: 2, d: 5 }, // 2 2/5 + 1 2/5 = 3 4/5
  { aW: 1, aN: 2, op: '+', bW: 1, bN: 2, d: 3 }, // 1 2/3 + 1 2/3 = 3 1/3 (cross whole)
  { aW: 2, aN: 1, op: '+', bW: 1, bN: 2, d: 4 }, // 2 1/4 + 1 2/4 = 3 3/4
];

function difficultyFor(s: Spec, answer: number): DifficultyLevel {
  const answerWholeOnly = answer % s.d === 0;
  if (answerWholeOnly) return DifficultyLevel.EASY;
  // Regroup = subtraction borrow or addition crossing a whole.
  const aFrac = s.aN, bFrac = s.bN;
  const regroup = s.op === '-' ? bFrac > aFrac : (aFrac + bFrac) >= s.d;
  return regroup ? DifficultyLevel.HARD : DifficultyLevel.MEDIUM;
}

function toRound(s: Spec, idx: number): TrekRound {
  const a = s.aW * s.d + s.aN;              // A in d-ths
  const b = s.bW * s.d + s.bN;              // B in d-ths
  const answer = s.op === '-' ? a - b : a + b;
  return {
    id: `trek-${idx}-${s.aW}_${s.aN}${s.op}${s.bW}_${s.bN}-${s.d}`,
    den: s.d,
    maxTick: MAX_TICK,
    op: s.op,
    aDths: a,
    bDths: b,
    startDths: a,
    answerDths: answer,
    difficulty: difficultyFor(s, answer)
  };
}

/**
 * Build a session's rounds: sort into an easy→hard gradient (shuffled
 * within each difficulty band) and sample `count` evenly across it.
 */
export function buildFractionTrekRounds(count: number): TrekRound[] {
  const rank: Record<DifficultyLevel, number> = {
    [DifficultyLevel.EASY]: 0,
    [DifficultyLevel.MEDIUM]: 1,
    [DifficultyLevel.HARD]: 2
  };
  const gradient = shuffleBands(POOL.map((s, i) => toRound(s, i)), r => rank[r.difficulty]);
  const n = gradient.length;
  const k = Math.min(count, n);
  if (k <= 0) return [];
  if (k === 1) return [gradient[0]];

  const used = new Set<number>();
  const out: TrekRound[] = [];
  for (let i = 0; i < k; i++) {
    let idx = Math.round((i * (n - 1)) / (k - 1));
    while (used.has(idx)) idx = (idx + 1) % n;
    used.add(idx);
    out.push(gradient[idx]);
  }
  return out;
}

/** Group by a numeric key (ascending), shuffle within each band. */
function shuffleBands(rounds: TrekRound[], keyFn: (r: TrekRound) => number): TrekRound[] {
  const bands = new Map<number, TrekRound[]>();
  for (const r of rounds) {
    const key = keyFn(r);
    const band = bands.get(key) ?? [];
    band.push(r);
    bands.set(key, band);
  }
  const out: TrekRound[] = [];
  [...bands.keys()].sort((a, b) => a - b).forEach(key => {
    const band = bands.get(key)!;
    for (let i = band.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [band[i], band[j]] = [band[j], band[i]];
    }
    out.push(...band);
  });
  return out;
}
